import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { MonthlyPoint } from '../../api/types';

interface Props {
  points: MonthlyPoint[];
  loading?: boolean;
  onMonthClick?: (month: string) => void;
}

const USERS_COLOR = '#6366f1';    // indigo 500
const SESSIONS_COLOR = '#10b981'; // emerald 500

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

export function MonthlyUsersSessionsChart({ points, loading, onMonthClick }: Props): JSX.Element {
  const months = points.map((p) => p.month);

  const option: EChartsOption = {
    grid: { left: 56, right: 64, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (raw) => formatNumber(Number(raw ?? 0)),
    },
    xAxis: {
      type: 'category',
      data: months,
    },
    yAxis: [
      {
        type: 'value',
        name: 'Active users',
        position: 'left',
        nameLocation: 'middle',
        nameGap: 44,
        minInterval: 1,
        axisLine: { lineStyle: { color: USERS_COLOR } },
      },
      {
        type: 'value',
        name: 'Sessions',
        position: 'right',
        nameLocation: 'middle',
        nameGap: 52,
        minInterval: 1,
        axisLine: { lineStyle: { color: SESSIONS_COLOR } },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Active users',
        type: 'bar',
        yAxisIndex: 0,
        barMaxWidth: 28,
        itemStyle: { color: USERS_COLOR },
        data: points.map((p) => p.activeUsers),
      },
      {
        name: 'Sessions',
        type: 'bar',
        yAxisIndex: 1,
        barMaxWidth: 28,
        itemStyle: { color: SESSIONS_COLOR },
        data: points.map((p) => p.sessions),
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 320, width: '100%', cursor: onMonthClick ? 'pointer' : 'default' }}
      showLoading={loading}
      notMerge
      lazyUpdate
      onEvents={
        onMonthClick
          ? {
              click: (params: { name?: string }) => {
                if (params?.name) onMonthClick(params.name);
              },
            }
          : undefined
      }
    />
  );
}
